import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Oscuro' },
  { value: 'system', label: 'Sistema' },
];

// Keep in sync with public/theme.js, which applies the theme before first paint.
const STORAGE_KEY = 'agents-theme';
const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)');

export function readThemePreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'dark' || saved === 'system' ? saved : 'light';
  } catch {
    return 'light';
  }
}

export function resolveTheme(pref: ThemePreference, systemDark: boolean): 'light' | 'dark' {
  return pref === 'dark' || (pref === 'system' && systemDark) ? 'dark' : 'light';
}

function apply(pref: ThemePreference) {
  document.documentElement.dataset.theme = resolveTheme(pref, darkQuery().matches);
}

/** Light is the product default; dark (Space Dark) and "follow the system" are opt-in. */
export function useTheme(): [ThemePreference, (pref: ThemePreference) => void] {
  const [pref, setPref] = useState(readThemePreference);

  useEffect(() => {
    apply(pref);
    if (pref !== 'system') return;
    const mq = darkQuery();
    const onChange = () => apply('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  const update = useCallback((next: ThemePreference) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage can be unavailable (private mode); the choice still applies to this session.
    }
    setPref(next);
  }, []);

  return [pref, update];
}
